import { AnchorHTMLAttributes, ButtonHTMLAttributes, ComponentType, Fragment, MouseEvent, ReactNode } from 'react'
import cx from 'classnames'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { Check, ChevronUp, SquareCheck, SquareDashed } from 'lucide-react'
import TealToolTip from 'components/TealToolTip'

// Mainly to be used for (styling) colours and icons, but can be used for any custom styling / overrides
interface Classes {
  button?: (({ open, focus, hover }: { open: boolean, focus: boolean, hover: boolean }) => string) | string
  chevron?: (({ open, hover, focus }: { open: boolean, hover: boolean, focus: boolean }) => string) | string
  menu?: (({ open }: { open: boolean }) => string) | string
  option?: (({ focus, selected }: { focus: boolean, selected: boolean }) => string) | string
  selectedIcon?: (({ focus, selected }: { focus: boolean, selected: boolean }) => string) | string
}

export interface DropdownProps {
  label: ComponentType<{ open: boolean }> | ReactNode
  handleOnSelect?: (value: string | number, e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
  preview?: boolean
  options: DropdownOptionT[]
  chevron?: boolean
  multi?: boolean
  align?: 'left' | 'right' | 'center'
  classes?: Classes
  showSelectedIcons?: boolean
  testId?: string
}

interface CommonDropdownOptionProps {
  label: ReactNode
  selected: boolean
  disabled?: boolean
  disabledReason?: string
}

export type DropdownOptionLinkT =
  CommonDropdownOptionProps
  & { value?: never }
  & AnchorHTMLAttributes<HTMLAnchorElement>
  & { href: string }

export type DropdownOptionButtonT =
  CommonDropdownOptionProps
  & { value: string | number }
  & ButtonHTMLAttributes<HTMLButtonElement>
  & { href?: never }

export type DropdownOptionT = DropdownOptionLinkT | DropdownOptionButtonT

function isLinkOption (option: DropdownOptionT): option is DropdownOptionLinkT {
  return (option as DropdownOptionLinkT).href !== undefined
}

type SelectedIconsProps =
  Pick<DropdownProps, 'multi' | 'classes'>
  & Pick<DropdownOptionT, 'selected'>
  & {
    className: Classes['selectedIcon']
    focus: boolean
  }

function SelectedIcons ({ focus, multi, selected, className }: SelectedIconsProps): JSX.Element {
  return (
    <>
      {multi === true && selected && (
        <SquareCheck className={typeof className === 'function' ? className({ focus, selected }) : className} />
      )}
      {multi === true && !selected && (
        <SquareDashed className={typeof className === 'function' ? className({ focus, selected }) : className} />
      )}
      {multi !== true && selected && (
        <Check className={typeof className === 'function' ? className({ focus, selected }) : className} />
      )}
    </>
  )
}

type DropdownOptionProps =
  {
    id: string
    children: ReactNode
    value: string | number
  }
  & Pick<DropdownProps, 'multi'>
  & Pick <DropdownOptionT, 'selected'>

function DropdownOption ({
  id,
  value,
  children,
  multi,
  selected
}: DropdownOptionProps): JSX.Element {
  return (
    <>
      <input
        hidden
        readOnly
        aria-hidden='true'
        checked={selected}
        type={multi === true ? 'checkbox' : 'radio'}
        name={id}
        id={id}
        value={value}
      />
      <label className='w-100 pointer'>
        {children}
      </label>
    </>
  )
}

export default function Dropdown ({
  label,
  chevron = true,
  options,
  handleOnSelect = () => null,
  multi = false,
  align,
  classes = {},
  showSelectedIcons = true,
  testId
}: DropdownProps): JSX.Element {
  if (!multi && options.filter(option => option.selected).length > 1) {
    throw new Error('Multiple options cannot be selected when multi is false')
  }

  const allLinks = options.every(option => 'href' in option)

  const handleOnSelectWrapper = (fn: typeof handleOnSelect) => (value: string | number, e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (multi) {
      e.preventDefault()
    }
    fn(value, e)
  }

  return (
    <Menu className='relative flex' as='div' data-testid={testId}>
      <>
        <MenuButton
          as={Fragment}
        >
          {({ open, hover, focus }) => {
            const isComponent = typeof label === 'function'
            const LabelComponent = label as ComponentType<{ open: boolean }>

            return (
              <button
                className={cx(
                  'ph5 pv4-half pointer flex items-center ph3-m pv2-m',
                  classes.button !== undefined
                    ? typeof classes.button === 'function'
                      ? classes.button({ open, hover, focus })
                      : classes.button
                    : 'bg-transparent'
                )}
              >
                <div className='grow-1 shrink-1 basis-auto'>
                  {isComponent ? <LabelComponent open={open} /> : label}
                </div>
                {chevron && (
                  <ChevronUp
                    className={cx(
                      'transition-all ml5 w6 hs6 min-w6',
                      { 'rotate-180': !open },
                      typeof classes.chevron === 'function'
                        ? classes.chevron({ open, hover, focus })
                        : classes.chevron
                    )}
                  />
                )}
              </button>
            )
          }}
        </MenuButton>
        <MenuItems
          as={allLinks ? 'nav' : 'div'}
          transition
          anchor={align === 'center' ? 'bottom' : align === 'left' ? 'bottom end' : 'bottom start'}
          className={cx(
            'shadow-faint-black outline-0 list-reset pa2 ma0 max-h24 o-auto z50 min-w-button',
            'fade-headless',
            typeof classes.menu === 'function' ? classes.menu({ open: false }) : classes.menu
          )}
        >
          {options.map((option, index) => {
            const { label } = option
            const value = isLinkOption(option) ? option.href : option.value
            const id = `${value ?? ''}-${index}`

            return (
              <MenuItem as={Fragment} key={`${id}-dropdown`} disabled={option.disabled}>
                {({ focus }) => {
                  const children = (
                    <>
                      {showSelectedIcons && <SelectedIcons multi={multi} selected={option.selected} focus={focus} className={classes.selectedIcon} />}
                      <DropdownOption id={id} multi={multi} selected={option.selected} value={value}>
                        {label}
                      </DropdownOption>
                    </>
                  )
                  const className = cx(
                    'db w-100 ph5 pv4-half br2 flex items-center gap5-half pointer tl',
                    typeof classes.option === 'function' ? classes.option({ focus, selected: option.selected }) : classes.option
                  )

                  if (option.disabled === true) {
                    return (
                      <button disabled className={cx(className, 'not-allowed o-50')} aria-disabled='true'>
                        {option.disabledReason !== undefined && option.disabledReason !== ''
                          ? (
                            <TealToolTip title={option.disabledReason} placement='left' arrow={false} bgColor='dark'>
                              <span className='flex items-center gap5-half w-100'>{children}</span>
                            </TealToolTip>
                            )
                          : children}
                      </button>
                    )
                  }

                  return isLinkOption(option)
                    ? (
                      <a
                        className={className}
                        href={option.href}
                        onClick={(e) => handleOnSelectWrapper(handleOnSelect)(option.href, e)}
                        target={option.target}
                        rel={option.rel}
                      >
                        {children}
                      </a>
                      )
                    : (
                      <button
                        className={className}
                        onClick={(e) => handleOnSelectWrapper(handleOnSelect)(option.value, e)}
                        type={option.type}
                      >
                        {children}
                      </button>
                      )
                }}
              </MenuItem>
            )
          })}
        </MenuItems>
      </>
    </Menu>
  )
}
